import {
  Component,
  Event,
  EventEmitter,
  Method,
  h,
  Host,
  Element,
  Prop,
  Listen,
} from '@stencil/core';
import { getActiveElement } from '../../utils/active-element';
import { getDirectChildren } from '../../utils/dom';
import { getTextContent } from '../../utils/slot';

type ValidElement = HTMLNanoNavItemElement | HTMLNanoOptionElement;
type NNI = HTMLNanoNavItemElement;

/**
 * Menu element. Commonly wrapped by `nano-dropdown` with `nano-option` and `nano-nav-item` elements nested.
 * Manages focus state / active.
 * @slot - The menu's content; `nano-option`, `nano-nav-item` elements as well as <hr> and more.
 */
@Component({
  tag: 'nano-menu',
  styleUrl: 'menu.scss',
  shadow: true,
})
export class Menu {
  private ignoreMouseEvents = false;
  private ignoreMouseTimeout: any;
  private menu: HTMLElement;
  private typeToSelect = '';
  private typeToSelectTimeout: any;

  @Element() private el: HTMLNanoMenuElement;

  // Public API

  /** get the focus state of the menu @readonly */
  @Prop()
  get hasFocus() {
    return this._hasFocus;
  }
  private _hasFocus = false;

  /** changes the role of the underlying control - sometimes required for semantics */
  @Prop() type: 'menu' | 'listbox' = 'menu';

  /** an accessible label */
  @Prop() label: string;

  // Events

  /** Emitted when the menu gains focus. */
  @Event() nanoFocus: EventEmitter;

  /** Emitted when the menu loses focus. */
  @Event() nanoBlur: EventEmitter;

  /** Emitted when a menu item is selected. */
  @Event() nanoSelect: EventEmitter<
    HTMLNanoOptionElement | HTMLNanoNavItemElement
  >;

  // Public Methods

  /** Sets focus on the menu. */
  @Method()
  async setFocus() {
    if (this.menu?.focus) this.menu.focus({ preventScroll: true });
  }

  /** Removes focus from the menu. */
  @Method()
  async removeFocus() {
    if (this.menu?.blur) this.menu.blur();
  }

  /** Sets the current active item */
  @Method()
  async showActiveElement() {
    if (this.selectedItem)
      this.selectedItem.scrollIntoView({ block: 'nearest' });
  }

  /** Removes any active item's state */
  @Method()
  async resetActiveItem() {
    this.getItems
      .filter((i) => i.tagName.toLowerCase() === 'nano-option')
      .map((i) => i.setAttribute('tabindex', '-1'));
  }

  // Private methods

  get getItems() {
    let items: ValidElement[];
    const opened = this.el.querySelectorAll('nano-nav-item.secondary-open');
    if (opened.length) {
      const ctx = opened[opened.length - 1] as HTMLNanoNavItemElement;
      items = Array.from(ctx.querySelectorAll('nano-nav-item, nano-option'));
    } else {
      items = getDirectChildren(
        this.el,
        'nano-nav-item, nano-option',
        'slot'
      ) as ValidElement[];
    }
    return items.filter((el) => !el.disabled && !el.hidden);
  }

  get activeItem() {
    const activeElement = getActiveElement();
    return this.getItems.find(
      (i) =>
        i.getAttribute('tabindex') === '0' ||
        i === activeElement ||
        i.classList.contains('has-focus')
    );
  }

  get selectedItem() {
    return this.getItems.find((i) => i.selected);
  }

  private async setActiveFocusItem(item?: ValidElement, focus = true) {
    const items = this.getItems;
    const activeItem =
      !item || item.disabled ? items.find((i) => i.selected) || items[0] : item;

    items
      .filter((i) => i.tagName.toLowerCase() === 'nano-option')
      .map((i: HTMLNanoOptionElement) =>
        i.setAttribute('tabindex', i === activeItem ? '0' : '-1')
      );

    if (!focus) return;

    if (activeItem) {
      (activeItem as NNI).setFocus
        ? (activeItem as NNI).setFocus()
        : activeItem.focus();
    } else this.menu.focus();
  }

  // Event Handlers

  @Listen('mousedown', { target: 'body' })
  @Listen('keydown')
  handleBlur(e: KeyboardEvent | MouseEvent) {
    if (!this.hasFocus) return;

    const kev = e as KeyboardEvent;
    let found: boolean;

    if (kev.key) {
      if (kev.key !== 'Tab') return;
      found =
        getActiveElement() &&
        getActiveElement().closest(this.el.tagName.toLowerCase()) === this.el;
    } else found = !!e.composedPath().find((el) => el === this.el);

    if (!found) {
      this.resetActiveItem();
      this._hasFocus = false;
      this.nanoBlur.emit();
    }
  }

  private handleFocus = () => {
    this.setActiveFocusItem(this.selectedItem || this.getItems[0]);

    this._hasFocus = true;
    this.nanoFocus.emit();
  };

  private handleClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const item = target.closest('nano-nav-item');

    if (item && !item.disabled) {
      this.nanoSelect.emit(item);
    }
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    // When keying through the menu, if the mouse happens to be hovering over a menu item and the menu scrolls, the
    // mouseout/mouseover event will fire causing the selection to be different than what the user expects. This gives
    // us a way to temporarily ignore mouse events while the user is interacting with a keyboard.
    clearTimeout(this.ignoreMouseTimeout);
    this.ignoreMouseTimeout = setTimeout(
      () => (this.ignoreMouseEvents = false),
      500
    );
    this.ignoreMouseEvents = true;

    // Make a selection when pressing enter
    switch (event.key) {
      case ' ':
        if (this.activeItem) this.activeItem.click();
        break;
      case 'ArrowDown':
      case 'ArrowUp':
      case 'PageDown':
      case 'PageUp':
      case 'Home':
      case 'End':
        const items = this.getItems;
        const selectedItem = this.activeItem;
        let index = items.indexOf(selectedItem);

        if (items.length) {
          event.preventDefault();

          if (event.key === 'ArrowDown') {
            index++;
          } else if (event.key === 'ArrowUp') {
            index--;
          } else if (event.key === 'Home' || event.key === 'PageUp') {
            index = 0;
          } else if (event.key === 'End' || event.key === 'PageDown') {
            index = items.length - 1;
          }

          if (index < 0) index = items.length - 1;
          if (index > items.length - 1) index = 0;

          this.setActiveFocusItem(items[index]);
          if (items[index]) items[index].scrollIntoView({ block: 'nearest' });
          return;
        }
        break;
    }

    // Handle type-to-search behavior when non-control characters are entered
    if (event.key === ' ' || /^[a-z0-9]+$/i.test(event.key)) {
      clearTimeout(this.typeToSelectTimeout);
      this.typeToSelectTimeout = setTimeout(
        () => (this.typeToSelect = ''),
        750
      );
      this.typeToSelect += event.key;

      const items = this.getItems;
      for (const item of items) {
        const slot = item.shadowRoot.querySelector(
          'slot:not([name])'
        ) as HTMLSlotElement;
        const label = getTextContent(slot).toLowerCase().trim();
        if (
          label.substring(0, this.typeToSelect.length) === this.typeToSelect
        ) {
          this.setActiveFocusItem(item);
          break;
        }
      }
    }
  };

  private handleMouseOver = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const item =
      target.closest('nano-nav-item') || target.closest('nano-option');

    if (item && !this.ignoreMouseEvents) {
      this.setActiveFocusItem(item);
    }
  };

  // Component Lifecycle

  render() {
    return (
      <Host>
        <div
          onClick={this.handleClick}
          onKeyDown={this.handleKeyDown}
          onMouseOver={this.handleMouseOver}
          onFocus={this.handleFocus}
          aria-label={this.label ? this.label : undefined}
          role={this.type}
          ref={(el) => (this.menu = el)}
          part="base"
          class={{
            menu: true,
            ['menu--' + this.type]: true,
            'menu--has-focus': this.hasFocus,
          }}
          tabIndex={-1}
        >
          <slot />
        </div>
      </Host>
    );
  }
}
